import { isPlaceholderEmail } from "@/lib/person-email";
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { Switch } from "@/components/design/Switch";
import { FileDropZone, type FileDropZoneHandle } from "@/components/common/FileDropZone";
import { DocumentView } from "@/components/documents/DocumentView";
import { AddToCollectionDialog } from "@/components/save/AddToCollectionDialog";
import { useContact } from "@/pages/save/use-contact";
import { useOrganizationalRecordAccess } from "@/pages/organizational-record/use-organizational-record-access";
import { useImportPowerAnalysis } from "./use-import-power-analysis";
import { ImportPowerResult } from "./ImportPowerResult";
import { ingestFiles, type IngestedFile } from "@/lib/file-ingest";
import { CV_DOCUMENT_ACCEPT, MAX_DOCUMENT_UPLOAD_BYTES } from "@/lib/upload-limits";
import { userErrorMessage } from "@/lib/user-error";
import { Permission, useHasPermission } from "@/auth/access-permissions";
import { ImportCvBatchDocument, MyPersonGroupsDocument } from "@/graphql/generated/operations";
import { CvImportOutcome } from "@/graphql/generated/types";

// Caps total sequential wait — each file is one importCvBatch round-trip.
const MAX_FILES = 25;

type ImportedRow = {
  fileName: string;
  outcome: CvImportOutcome;
  personId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

/**
 * Roster import (admin, System-section only): drop many CVs, then Import runs
 * genesis `importCvBatch` — creating/updating a Person per CV (fill-empty). The
 * SPA sends **one CV per call, sequentially, with progress** so a long LLM
 * extraction can't trip the BFF request timeout and a single failure never
 * aborts the rest. Nothing here touches the caller's own vault documents.
 *
 * Optional switch "Analyse powers" (@adr SPARK-0049-roster-import-power-analysis-goes-through-the-organizational-record@1):
 * each imported person's CV is filed in their organizational record and analysed there, at most two
 * at a time, while the import goes on. Proposals are reviewed in the result rows and applied on request.
 */
export function BulkCvImport() {
  const intl = useIntl();
  // The broad import capability Genesis enforces also controls whether this
  // admin section is shown. Specialist person-edit rights are deliberately
  // not an extra switch.
  const hasImportPermission = useHasPermission(Permission.WriteImport);
  const zoneRef = useRef<FileDropZoneHandle>(null);
  const [files, setFiles] = useState<IngestedFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // Die ART des Hinweises wandert mit, damit ein Test ihn nicht am deutschen
  // Text auseinanderhalten muss.
  const [note, setNote] = useState<{ kind: "skipped" | "tooMany"; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ImportedRow[] | null>(null);
  const [importPhoto, setImportPhoto] = useState(true);
  const [importCvBatch] = useMutation(ImportCvBatchDocument);

  // Community assignment (same action as the profile "+ Community": files the
  // imported persons into a PersonGroup via addContacts). Entirely optional.
  const contact = useContact();
  const { data: groupsData } = useQuery(MyPersonGroupsDocument, { variables: { input: {} } });
  const communities = useMemo(() => {
    const pg = groupsData?.personGroups;
    return pg?.__typename === "PersonGroupResult" ? pg.personGroups.map((g) => ({ id: g.id, name: g.name })) : [];
  }, [groupsData]);
  const [pendingGroups, setPendingGroups] = useState<string[]>([]); // chosen before import, applied after
  const [communityDialog, setCommunityDialog] = useState<"pre" | "post" | null>(null);
  const [successfulIds, setSuccessfulIds] = useState<string[]>([]); // person ids imported this batch (not failed)
  const [communityNote, setCommunityNote] = useState<string | null>(null);

  // Power analysis per imported person, switched on before the import like the photo. Filing into
  // the record needs write access, the analysis its own right; without both the switch is absent
  // and so are any proposals already on screen (they quote private record content).
  const recordAccess = useOrganizationalRecordAccess();
  const canAnalyse = recordAccess.canWrite && recordAccess.canAnalyzePowers === true;
  const [analysePowers, setAnalysePowers] = useState(false);
  const power = useImportPowerAnalysis();

  const groupNames = (ids: string[]) =>
    ids.map((id) => communities.find((c) => c.id === id)?.name).filter(Boolean).join(", ");

  // Roster import gate (see @adr SPARK-0027-roster-import-auto-persists-fill-empty@2):
  // write:import is also one of the Permissions that opens System settings.
  if (!hasImportPermission) return null;

  const addFiles = async (fl: FileList) => {
    setError(null);
    // The last run's rows stay until the next run starts: they hold power proposals that can only
    // be applied from here.
    const { accepted, rejected } = await ingestFiles(fl, {
      maxBytes: MAX_DOCUMENT_UPLOAD_BYTES,
      accept: CV_DOCUMENT_ACCEPT,
    });
    const room = MAX_FILES - files.length;
    const toAdd = accepted.slice(0, Math.max(0, room));
    const overflow = accepted.length - toAdd.length;
    setFiles((prev) => [...prev, ...toAdd]);
    if (rejected.length > 0) {
      setNote({
        kind: "skipped",
        text: intl.formatMessage({ id: "service.page.bulkCv.rejected", defaultMessage: "Some files were skipped (too large or unsupported type)." }),
      });
    } else if (overflow > 0) {
      setNote({
        kind: "tooMany",
        text: intl.formatMessage({ id: "service.page.bulkCv.tooMany", defaultMessage: "You can import at most {max} files at once." }, { max: MAX_FILES }),
      });
    } else {
      setNote(null);
    }
  };

  const removeFile = (index: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const runImport = async () => {
    if (busy || files.length === 0 || power.active) return;
    setBusy(true);
    setError(null);
    setNote(null);
    setCommunityNote(null);
    setSuccessfulIds([]);
    power.reset();
    const withPowers = analysePowers && canAnalyse;
    setResults([]); // publish incrementally so each person link appears as its file completes
    // Files can't change during the run (zone + buttons are disabled while busy),
    // so this batch stays index-aligned with `rows`.
    const batch = files;
    const rows: ImportedRow[] = [];
    let firstError: string | null = null;
    setProgress({ done: 0, total: batch.length });
    try {
      for (const [i, f] of batch.entries()) {
        try {
          const res = await importCvBatch({
            variables: { files: [{ file: f.content, fileName: f.name }], importPhoto },
          });
          const row = res.data?.importCvBatch.results[0];
          rows.push(
            row
              ? {
                  fileName: row.fileName,
                  outcome: row.outcome,
                  personId: row.personId,
                  firstName: row.firstName,
                  lastName: row.lastName,
                  email: row.email,
                }
              : { fileName: f.name, outcome: CvImportOutcome.Failed },
          );
          // Starts as soon as this person exists; the next CV imports meanwhile.
          if (withPowers && row?.personId && row.outcome !== CvImportOutcome.Failed) {
            power.enqueue(String(i), row.personId, f);
          }
        } catch (e) {
          // Per-file isolation: one failure is recorded and the loop continues.
          // The first thrown error (e.g. a permission 403) is surfaced once.
          if (firstError === null) firstError = userErrorMessage(intl, e);
          rows.push({ fileName: f.name, outcome: CvImportOutcome.Failed });
        }
        setResults([...rows]); // new ref -> the link for this file shows immediately
        setProgress({ done: i + 1, total: batch.length });
      }
      setError(firstError);
      // Keep the files that failed so they can be retried; drop the succeeded
      // ones (re-importing a success would create a duplicate Person).
      setFiles(batch.filter((_, i) => rows[i]?.outcome === CvImportOutcome.Failed));

      const ids = rows
        .filter((r) => r.personId && r.outcome !== CvImportOutcome.Failed)
        .map((r) => r.personId as string);
      setSuccessfulIds(ids);
      // Auto-apply the pre-import community choice. Best-effort: a failure is noted
      // but never fails the import (the persons are already created).
      if (pendingGroups.length > 0 && ids.length > 0) {
        try {
          await contact.add({ contactIds: ids, groups: pendingGroups });
          setCommunityNote(
            intl.formatMessage(
              { id: "service.page.bulkCv.addedToCommunity", defaultMessage: "Added {n} to {names}" },
              { n: ids.length, names: groupNames(pendingGroups) },
            ),
          );
        } catch (e) {
          setCommunityNote(userErrorMessage(intl, e));
        }
      }
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const counts = results
    ? {
        created: results.filter((r) => r.outcome === CvImportOutcome.Created).length,
        updated: results.filter((r) => r.outcome === CvImportOutcome.Updated).length,
        failed: results.filter((r) => r.outcome === CvImportOutcome.Failed).length,
      }
    : null;

  return (
    <div id="cv-import" className="box bg-surface p-6">
      <BoxHeader
        title={intl.formatMessage({ id: "service.page.bulkCv.title", defaultMessage: "Roster import" })}
        variant="hero"
      />
      <div className="smycles-field-meta leading-snug text-ink">
        <FormattedMessage
          id="service.page.bulkCv.desc"
          defaultMessage="Drop multiple CVs to create or update the corresponding profiles. Existing profiles are only filled where empty – nothing is overwritten."
        />
      </div>

      <div className="mt-5">
        <FileDropZone
          id="cv-import-file-input-zone"
          inputId="cv-import-file-input"
          ref={zoneRef}
          variant="zone"
          accept={CV_DOCUMENT_ACCEPT}
          multiple
          disabled={busy}
          clickToOpen={files.length === 0}
          onFiles={(fl) => void addFiles(fl)}
          label={<FormattedMessage id="service.page.bulkCv.zone" defaultMessage="Drag CVs here or click to add" />}
          hint={<FormattedMessage id="service.page.bulkCv.hint" defaultMessage="PDF, DOC, DOCX, TXT, RTF, HTML, images · max. 10 MB per file · up to 25 files" />}
        >
          {files.length > 0 ? (
            <div className="grid grid-cols-3 gap-2 p-3 @sm:grid-cols-4 @lg:grid-cols-6">
              {files.map((f, i) => (
                <DocumentView variant="tile"
                  file={f}
                  key={`${f.name}-${i}`}
                  // Dateiname UND Platz: derselbe Schlüssel wie der React-Key,
                  // denn ein Stapel darf zweimal dieselbe Datei enthalten.
                  // Eigener Namensraum: der feste `cv-import-file-input` (die
                  // Dateiauswahl) läge sonst im Raum der Dateinamen.
                  idPrefix={`cv-import-tile-${f.name}-${i}`}
                  name={f.name}
                  sizeBytes={f.size}
                  onRemove={busy ? undefined : () => removeFile(i)}
                />
              ))}
            </div>
          ) : null}
        </FileDropZone>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button id="cv-import-add-files" variant="secondary" disabled={busy} onClick={() => zoneRef.current?.openPicker()}>
          + <FormattedMessage id="service.page.bulkCv.addMore" defaultMessage="Add files" />
        </Button>
        <Button id="cv-import-start" variant="primary" disabled={busy || files.length === 0 || power.active} onClick={() => void runImport()}>
          {busy && progress ? (
            <span id="cv-import-progress-text" className="inline-flex items-center gap-2">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
              <FormattedMessage id="service.page.bulkCv.importing" defaultMessage="Importing {done}/{total} …" values={{ done: progress.done, total: progress.total }} />
            </span>
          ) : (
            <FormattedMessage id="service.page.bulkCv.importBtn" defaultMessage="Import {n, plural, one {# CV} other {# CVs}}" values={{ n: files.length }} />
          )}
        </Button>
        <label className="flex items-center gap-2 text-ink">
          <Switch
            id="cv-import-photo"
            checked={importPhoto}
            onChange={setImportPhoto}
            ariaLabel={intl.formatMessage({ id: "service.page.bulkCv.importPhoto", defaultMessage: "Import photo" })}
          />
          <FormattedMessage id="service.page.bulkCv.importPhoto" defaultMessage="Import photo" />
        </label>
        {canAnalyse ? (
          <label className="flex items-center gap-2 text-ink">
            <Switch
              id="cv-import-powers"
              checked={analysePowers}
              onChange={setAnalysePowers}
              // Chosen for the next run: switching it mid-run would apply to only part of the batch.
              disabled={busy}
              ariaLabel={intl.formatMessage({ id: "service.page.bulkCv.analysePowers", defaultMessage: "Analyse powers" })}
            />
            <FormattedMessage id="service.page.bulkCv.analysePowers" defaultMessage="Analyse powers" />
          </label>
        ) : null}
        {communities.length > 0 ? (
          <Button id="cv-import-community" variant="secondary" disabled={busy} onClick={() => setCommunityDialog("pre")}>
            + <FormattedMessage id="service.page.bulkCv.addToCommunity" defaultMessage="Community" />
          </Button>
        ) : null}
      </div>

      {pendingGroups.length > 0 ? (
        <div id="cv-import-collection-hint" className="mt-2 text-smycles-meta text-ink">
          <FormattedMessage
            id="service.page.bulkCv.willAddToCommunity"
            defaultMessage="New imports will be added to: {names}"
            values={{ names: groupNames(pendingGroups) }}
          />
        </div>
      ) : null}

      {canAnalyse && analysePowers ? (
        <div id="cv-import-power-hint" className="mt-2 text-smycles-meta text-ink">
          <FormattedMessage
            id="service.page.bulkCv.willAnalysePowers"
            defaultMessage="After the import, each CV goes into the person's organizational record and is analysed for powers (PDF, DOCX and images only)."
          />
        </div>
      ) : null}
      {canAnalyse && power.active ? (
        <div id="cv-import-power-keep-open" className="mt-2 text-smycles-meta text-ink">
          <FormattedMessage
            id="service.page.bulkCv.powerKeepOpen"
            defaultMessage="Keep this page open until the power analyses are done: proposals can only be applied here."
          />
        </div>
      ) : null}

      {/* Working-in-the-background feedback: a determinate bar that fills as each CV completes. */}
      {busy && progress ? (
        <div
          id="cv-import-progress"
          className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-ink/10"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={progress.total}
          aria-valuenow={progress.done}
        >
          <div
            className="h-full rounded-full bg-accent transition-[width] duration-300 ease-out"
            style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
          />
        </div>
      ) : null}

      <div id="cv-import-duplicate-hint" className="smycles-field-label mt-2 text-ink">
        <FormattedMessage
          id="service.page.bulkCv.dupWarning"
          defaultMessage="CVs without an email create a new profile on every run. Avoid re-importing the same files."
        />
      </div>

      {note ? <div id={`cv-import-note-${note.kind}`} className="mt-2 text-smycles-meta text-ink">{note.text}</div> : null}
      {error ? <div id="cv-import-error" className="mt-2 text-smycles-meta text-red-600">{error}</div> : null}

      {results && counts ? (
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <div id="cv-import-summary" className="smycles-field-value font-medium">
              <FormattedMessage
                id="service.page.bulkCv.summary"
                defaultMessage="{created} created, {updated} updated, {failed} failed"
                values={counts}
              />
            </div>
            {!busy && successfulIds.length > 0 && communities.length > 0 ? (
              <Button variant="secondary" onClick={() => setCommunityDialog("post")}>
                + <FormattedMessage id="service.page.bulkCv.addToCommunity" defaultMessage="Community" />
              </Button>
            ) : null}
            {communityNote ? <span className="text-smycles-meta text-ink">{communityNote}</span> : null}
          </div>
          {canAnalyse && power.readyKeys.length > 0 ? (
            <div id="cv-import-power-bulk" className="mt-2 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-smycles-meta text-ink">
                <input
                  id="cv-import-power-select-all"
                  type="checkbox"
                  checked={power.selected.size === power.readyKeys.length}
                  disabled={power.applying}
                  onChange={() => power.setAllSelected(power.selected.size !== power.readyKeys.length)}
                  className="h-4 w-4 shrink-0 accent-ink disabled:opacity-30"
                />
                <FormattedMessage id="service.page.bulkCv.powerSelectAll" defaultMessage="Select all proposals" />
              </label>
              <Button
                id="cv-import-power-apply"
                variant="primary"
                disabled={power.applying || power.selected.size === 0}
                onClick={() => void power.applySelected()}
              >
                <FormattedMessage
                  id="service.page.bulkCv.powerApply"
                  defaultMessage="Apply selected to profiles ({n})"
                  values={{ n: power.selected.size }}
                />
              </Button>
            </div>
          ) : null}
          <ul className="mt-2 space-y-1.5 text-smycles-meta">
            {results.map((r, i) => {
              const name = [r.firstName, r.lastName].filter(Boolean).join(" ").trim();
              const created = r.outcome === CvImportOutcome.Created;
              const nameLabel =
                name || intl.formatMessage({ id: "service.page.bulkCv.openProfile", defaultMessage: "profile" });
              const powerRow = power.rows[String(i)];
              return (
                <li
                  key={`${r.fileName}-${i}`}
                  id={`cv-import-result-${r.fileName}-${i}`}
                  className="flex flex-wrap items-center gap-x-2 gap-y-1"
                >
                  <span className="truncate text-ink">{r.fileName}</span>
                  <span className="text-ink-muted">→</span>
                  {r.outcome === CvImportOutcome.Failed ? (
                    <span className="rounded px-1.5 py-0.5 text-smycles-micro font-semibold uppercase tracking-wide bg-red-500/10 text-red-600">
                      <FormattedMessage id="service.page.bulkCv.outcomeFailed" defaultMessage="couldn't import" />
                    </span>
                  ) : (
                    <>
                      {/* Per-file status: created (new person) vs updated (matched existing). */}
                      <span
                        className={`rounded px-1.5 py-0.5 text-smycles-micro font-semibold uppercase tracking-wide ${
                          created ? "bg-accent/15 text-ink-accent" : "bg-ink/10 text-ink"
                        }`}
                      >
                        {created ? (
                          <FormattedMessage id="service.page.bulkCv.outcomeCreated" defaultMessage="New" />
                        ) : (
                          <FormattedMessage id="service.page.bulkCv.outcomeUpdated" defaultMessage="Updated" />
                        )}
                      </span>
                      {r.personId ? (
                        <Link id={`cv-import-resultlink-${r.fileName}-${i}`} to={`/p/${r.personId}`} className="font-medium text-ink-accent underline">
                          {nameLabel}
                        </Link>
                      ) : (
                        <span className="font-medium text-ink">{nameLabel}</span>
                      )}
                      <span className="text-ink-muted">
                        {r.email && !isPlaceholderEmail(r.email) ? r.email : (
                          <FormattedMessage id="person.email.missing" defaultMessage="No email address provided" />
                        )}
                      </span>
                      {canAnalyse && powerRow ? (
                        <ImportPowerResult
                          index={i}
                          row={powerRow}
                          selected={power.selected.has(String(i))}
                          onToggle={() => power.toggle(String(i))}
                          disabled={power.applying}
                          subject={nameLabel}
                        />
                      ) : null}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {/* Pre-import: defer mode — records the chosen community; applied after the import completes. */}
      <AddToCollectionDialog
        id="add-to-collection-pre"
        open={communityDialog === "pre"}
        onClose={() => setCommunityDialog(null)}
        target="community"
        contactIds={[]}
        personName={intl.formatMessage(
          { id: "service.page.bulkCv.communitySubject", defaultMessage: "{n} imported people" },
          { n: files.length },
        )}
        onConfirmGroups={(ids) => {
          setPendingGroups(ids);
          setCommunityDialog(null);
        }}
      />
      {/* Post-import: same dialog as the profile card — adds the imported persons immediately. */}
      <AddToCollectionDialog
        id="add-to-collection-post"
        open={communityDialog === "post"}
        onClose={() => setCommunityDialog(null)}
        target="community"
        contactIds={successfulIds}
        personName={intl.formatMessage(
          { id: "service.page.bulkCv.communitySubject", defaultMessage: "{n} imported people" },
          { n: successfulIds.length },
        )}
      />
    </div>
  );
}
